import { useCallback, useEffect, useState } from 'react';
import {
  AuthHeader,
  Button,
  FormContextProvider,
  Input,
  InputField,
  useToastNotifications,
} from '@nbirl/nbi-common-ui-libs';
import { Form, FormikHelpers } from 'formik';

import { TextButton } from '../../../../shared/components';
import { useAuth } from '../../../../shared/context';
import { useCognitoUser, useErrorToast } from '../../../../shared/hooks';
import { validateEmailFormSchema } from './validate-email.form';
import { ValidateEmailFormValues } from './validate-email-form.model';

type ValidateEmailFormProps = {
  isPending: boolean;
  onSubmit: (
    { code }: ValidateEmailFormValues,
    formikHelpers: FormikHelpers<ValidateEmailFormValues>
  ) => void;
};

export const ValidateEmailForm = ({
  isPending,
  onSubmit,
}: ValidateEmailFormProps) => {
  const user = useCognitoUser();
  const { validateEmail, signOut } = useAuth();
  const { addToast } = useToastNotifications();
  const { showErrorToast } = useErrorToast();
  const [isSendingCode, setIsSendingCode] = useState(false);

  const sendVerificationEmail = useCallback(
    async (showSuccessToast = false) => {
      try {
        setIsSendingCode(true);
        await validateEmail();

        if (showSuccessToast) {
          addToast({
            variant: 'success',
            text: 'New verification code sent.',
          });
        }
      } catch (error: any) {
        showErrorToast(error);
      } finally {
        setIsSendingCode(false);
      }
    },
    [validateEmail, addToast, showErrorToast]
  );

  useEffect(() => {
    sendVerificationEmail();
  }, [sendVerificationEmail]);

  return (
    <FormContextProvider<ValidateEmailFormValues>
      initialValues={{ code: '' }}
      validationSchema={validateEmailFormSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <AuthHeader
          heading="Validate email address"
          helpText={`We have delivered the verification code to ${user.email}. Please enter the code to validate your email.`}
        />
        <div className="space-y-4 mb-8">
          <InputField name="code" showRequiredIndicator={false}>
            {(field) => <Input {...field} />}
          </InputField>
        </div>
        <div className="flex items-center space-x-2">
          <TextButton
            disabled={isSendingCode}
            onClick={() => sendVerificationEmail(true)}
          >
            Send new code
          </TextButton>
          <div className="flex-1" />
          <Button variant="outline" onClick={signOut}>
            Back to login
          </Button>
          <Button type="submit" isPending={isPending}>
            Confirm account
          </Button>
        </div>
      </Form>
    </FormContextProvider>
  );
};
